import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, mergeProps as _mergeProps, resolveDirective as _resolveDirective, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, createBlock as _createBlock } from "vue"

const _hoisted_1 = /*#__PURE__*/_createTextVNode("Testbed")
const _hoisted_2 = /*#__PURE__*/_createTextVNode("Category Icons")
const _hoisted_3 = /*#__PURE__*/_createTextVNode("Ring")
const _hoisted_4 = /*#__PURE__*/_createTextVNode("Textbox")
const _hoisted_5 = /*#__PURE__*/_createTextVNode("Single Select")
const _hoisted_6 = /*#__PURE__*/_createTextVNode("Multi Select")
const _hoisted_7 = /*#__PURE__*/_createTextVNode("Tags Select")
const _hoisted_8 = /*#__PURE__*/_createTextVNode("Autocomplete Select")
const _hoisted_9 = /*#__PURE__*/_createTextVNode("Button")
const _hoisted_10 = /*#__PURE__*/_createTextVNode("Tabs")
const _hoisted_11 = /*#__PURE__*/_createTextVNode("Status")
const _hoisted_12 = /*#__PURE__*/_createTextVNode("Loading some results")
const _hoisted_13 = /*#__PURE__*/_createTextVNode("Action was a success")
const _hoisted_14 = /*#__PURE__*/_createTextVNode("Be careful")
const _hoisted_15 = /*#__PURE__*/_createTextVNode("There was an error")
const _hoisted_16 = /*#__PURE__*/_createTextVNode("Action is paused")
const _hoisted_17 = /*#__PURE__*/_createTextVNode("Unexpected result")
const _hoisted_18 = /*#__PURE__*/_createTextVNode("Table")
const _hoisted_19 = /*#__PURE__*/_createTextVNode("Arbitrary slot content")

import { ref } from "vue";
import { omit } from "lodash";
import AppButton from "@/components/AppButton.vue";
import AppInput from "@/components/AppInput.vue";
import AppRing from "@/components/AppRing.vue";
import AppSelectAutocomplete from "@/components/AppSelectAutocomplete.vue";
import AppSelectMulti from "@/components/AppSelectMulti.vue";
import AppSelectSingle from "@/components/AppSelectSingle.vue";
import AppSelectTags from "@/components/AppSelectTags.vue";
import AppTable from "@/components/AppTable.vue";
import AppTabs from "@/components/AppTabs.vue";
import AppTextbox from "@/components/AppTextbox.vue";
import { Cols, Rows, Sort } from "@/components/AppTable";
import { sleep } from "@/util/debug";
import { Filters } from "@/api/facets";

type ButtonProps = InstanceType<typeof AppButton>["$props"];

/** enumerate permutations of button options */

export default /*#__PURE__*/_defineComponent({
  __name: 'PageTestbed',
  setup(__props) {

const input = ref("");

const buttons = ref<Array<Array<ButtonProps>>>([]);
for (const design of ["normal", "circle", "small"]) {
  for (const color of ["primary", "secondary"]) {
    const row = [];
    for (const [text, icon] of [
      ["Text", ""],
      ["Text", "download"],
      ["", "download"],
    ]) {
      row.push({ design, color, text, icon } as ButtonProps);
    }
    buttons.value.push(row);
  }
}

/** single select */
const singleSelectOptions = ref([
  { id: "apple", icon: "lightbulb" },
  { id: "banana", icon: "lightbulb" },
  { id: "cherry", icon: "lightbulb", count: 54 },
  { id: "durian", icon: "lightbulb", count: 54 },
  { id: "elderberry", count: 54 },
  { id: "fig", count: 54 },
  { id: "grape" },
  { id: "honeydew" },
]);
const singleSelectValue = ref({ id: "durian" });

/** multi select */
const multiSelectOptions = ref([
  { id: "fruits", count: 0 },
  { id: "vegetables", count: 7 },
  { id: "colors", count: 42 },
  { id: "animals", count: 999 },
  { id: "cars" },
  { id: "schools" },
  { id: "appliances" },
]);
const multiSelectValue = ref([{ id: "vegetables" }]);

/** tags select */
const tagsSelectOptions = ref(async (search = "") => {
  await sleep(500); /** test loading spinner */
  return {
    options: [
      { id: "ice cream", icon: "home" },
      { id: "candy", icon: "database", info: "8 phenotypes" },
      { id: "gummies", info: "4 phenotypes" },
      { id: "brownies", icon: "puzzle-piece", info: "1 phenotype" },
      { id: "cookies" },
    ].filter(({ id }) => id.includes(search)),
    message: "Selected item!",
  };
});
const tagsSelectValue = ref([
  { id: "candy", icon: "database", count: "8 phenotypes" },
]);

/** autocomplete select */
const autocompleteSelectOptions = ref(async () => {
  await sleep(500); /** test loading spinner */
  return [
    { icon: "home", name: "Cat" },
    { name: "Dog", info: "good dog" },
    { icon: "download", name: "Zebra" },
  ];
});

/** tabs */
const tabs = [
  { id: "apple", text: "Apple", icon: "asterisk" },
  { id: "banana", text: "Banana", icon: "cogs" },
  { id: "cherry", text: "Cherry", icon: "home" },
  { id: "durian", text: "Durian", icon: "puzzle-piece" },
  { id: "elderberry", text: "Elderberry", icon: "tools" },
];

/** selected tab */
const tab = ref(tabs[0].id);

/** table input props */
const table = ref({
  cols: [
    {
      id: "name",
      key: "name",
      heading: "Name",
      align: "left",
      sortable: true,
    },
    {
      id: "score",
      key: "score",
      heading: "Score",

      sortable: true,
    },
    {
      id: "details",
      key: "details",
      heading: "Details",
      align: "left",
      sortable: true,
    },
    {
      id: "arbitrary",
      key: "arbitrary",
      heading: "Arbitrary",
      align: "right",
    },
  ] as Cols,
  rows: [
    { name: "abc", score: 9, details: [1, 2] },
    { name: "def", score: -1, details: [2, 1, 3] },
    { name: "def", score: 2, details: [1] },
    { name: "abc", score: 4, details: [2, 1] },
    { name: "ghi", score: NaN, details: [1] },
  ] as Rows,
  sort: { id: "score", direction: "up" } as Sort,
  perPage: 10,
  start: 0,
  end: 11,
  total: 123,
  search: "",
  availableFilters: { score: [{ id: "numbers" }, { id: "nulls" }] } as Filters,
  activeFilters: { score: [{ id: "numbers" }] } as Filters,
});

/** util */
const log = console.info;

return (_ctx: any,_cache: any) => {
  const _component_AppHeading = _resolveComponent("AppHeading")!
  const _component_AppSection = _resolveComponent("AppSection")!
  const _component_AppIcon = _resolveComponent("AppIcon")!
  const _component_AppFlex = _resolveComponent("AppFlex")!
  const _component_AppStatus = _resolveComponent("AppStatus")!
  const _component_AppGallery = _resolveComponent("AppGallery")!
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_AppSection, null, {
      default: _withCtx(() => [
        _createVNode(_component_AppHeading, null, {
          default: _withCtx(() => [
            _hoisted_1
          ]),
          _: 1
        })
      ]),
      _: 1
    }),
    _createVNode(_component_AppSection, null, {
      default: _withCtx(() => [
        _createVNode(_component_AppHeading, null, {
          default: _withCtx(() => [
            _hoisted_2
          ]),
          _: 1
        }),
        _createVNode(_component_AppFlex, null, {
          default: _withCtx(() => [
            _createVNode(_component_AppIcon, { icon: "category-anatomy" }),
            _createVNode(_component_AppIcon, { icon: "category-case" }),
            _createVNode(_component_AppIcon, { icon: "category-cell-line" }),
            _createVNode(_component_AppIcon, { icon: "category-disease" }),
            _createVNode(_component_AppIcon, { icon: "category-fallback" }),
            _createVNode(_component_AppIcon, { icon: "category-function" }),
            _createVNode(_component_AppIcon, { icon: "category-gene" }),
            _createVNode(_component_AppIcon, { icon: "category-genotype" }),
            _createVNode(_component_AppIcon, { icon: "category-homolog" }),
            _createVNode(_component_AppIcon, { icon: "category-interaction" }),
            _createVNode(_component_AppIcon, { icon: "category-model" }),
            _createVNode(_component_AppIcon, { icon: "category-ortholog-disease" }),
            _createVNode(_component_AppIcon, { icon: "category-ortholog-phenotype" }),
            _createVNode(_component_AppIcon, { icon: "category-pathway" }),
            _createVNode(_component_AppIcon, { icon: "category-phenotype" }),
            _createVNode(_component_AppIcon, { icon: "category-unknown" }),
            _createVNode(_component_AppIcon, { icon: "category-variant" })
          ]),
          _: 1
        })
      ]),
      _: 1
    }),
    _createVNode(_component_AppSection, null, {
      default: _withCtx(() => [
        _createVNode(_component_AppHeading, null, {
          default: _withCtx(() => [
            _hoisted_3
          ]),
          _: 1
        }),
        _createVNode(AppRing)
      ]),
      _: 1
    }),
    _createVNode(_component_AppSection, null, {
      default: _withCtx(() => [
        _createVNode(_component_AppHeading, null, {
          default: _withCtx(() => [
            _hoisted_4
          ]),
          _: 1
        }),
        _createElementVNode("label", null, [
          _createVNode(AppInput, {
            modelValue: input.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((input).value = $event)),
            placeholder: "Raw input"
          }, null, 8, ["modelValue"])
        ]),
        _createVNode(AppTextbox, {
          modelValue: input.value,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((input).value = $event)),
          icon: "search",
          placeholder: "Single line input"
        }, null, 8, ["modelValue"]),
        _createVNode(AppTextbox, {
          modelValue: input.value,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((input).value = $event)),
          multi: true,
          icon: "search",
          placeholder: "Multi-line input"
        }, null, 8, ["modelValue"])
      ]),
      _: 1
    }),
    _createVNode(_component_AppSection, null, {
      default: _withCtx(() => [
        _createVNode(_component_AppHeading, null, {
          default: _withCtx(() => [
            _hoisted_5
          ]),
          _: 1
        }),
        _createElementVNode("span", null, _toDisplayString(singleSelectValue.value), 1),
        _createVNode(AppSelectSingle, {
          modelValue: singleSelectValue.value,
          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((singleSelectValue).value = $event)),
          name: "Fruits",
          options: singleSelectOptions.value
        }, null, 8, ["modelValue", "options"])
      ]),
      _: 1
    }),
    _createVNode(_component_AppSection, null, {
      default: _withCtx(() => [
        _createVNode(_component_AppHeading, null, {
          default: _withCtx(() => [
            _hoisted_6
          ]),
          _: 1
        }),
        _createElementVNode("span", null, _toDisplayString(multiSelectValue.value), 1),
        _createVNode(AppSelectMulti, {
          modelValue: multiSelectValue.value,
          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((multiSelectValue).value = $event)),
          name: "Category",
          options: multiSelectOptions.value
        }, null, 8, ["modelValue", "options"]),
        _createVNode(AppSelectMulti, {
          modelValue: multiSelectValue.value,
          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((multiSelectValue).value = $event)),
          name: "Category",
          design: "small",
          options: multiSelectOptions.value
        }, {
          default: _withCtx((props) => [
            _withDirectives(_createVNode(AppButton, _mergeProps({ icon: "filter" }, props, { design: "small" }), null, 16), [
              [_directive_tooltip, 'Test button']
            ])
          ]),
          _: 1
        }, 8, ["modelValue", "options"])
      ]),
      _: 1
    }),
    _createVNode(_component_AppSection, null, {
      default: _withCtx(() => [
        _createVNode(_component_AppHeading, null, {
          default: _withCtx(() => [
            _hoisted_7
          ]),
          _: 1
        }),
        _createElementVNode("span", null, _toDisplayString(tagsSelectValue.value), 1),
        _createVNode(AppSelectTags, {
          modelValue: tagsSelectValue.value,
          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((tagsSelectValue).value = $event)),
          name: "Desserts",
          placeholder: "Search for a dessert",
          options: tagsSelectOptions.value
        }, null, 8, ["modelValue", "options"])
      ]),
      _: 1
    }),
    _createVNode(_component_AppSection, null, {
      default: _withCtx(() => [
        _createVNode(_component_AppHeading, null, {
          default: _withCtx(() => [
            _hoisted_8
          ]),
          _: 1
        }),
        _createVNode(AppSelectAutocomplete, {
          name: "Animals",
          options: autocompleteSelectOptions.value
        }, null, 8, ["options"])
      ]),
      _: 1
    }),
    _createVNode(_component_AppSection, null, {
      default: _withCtx(() => [
        _createVNode(_component_AppHeading, null, {
          default: _withCtx(() => [
            _hoisted_9
          ]),
          _: 1
        }),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(buttons.value, (row, rowIndex) => {
          return (_openBlock(), _createBlock(_component_AppFlex, { key: rowIndex }, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(row, (props, colIndex) => {
                return _withDirectives((_openBlock(), _createBlock(AppButton, _mergeProps({
                  key: colIndex,
                  to: "/"
                }, props, { onClick: _unref(log) }), null, 16, ["onClick"])), [
                  [_directive_tooltip, 'Test button']
                ])
              }), 128))
            ]),
            _: 2
          }, 1024))
        }), 128))
      ]),
      _: 1
    }),
    _createVNode(_component_AppSection, null, {
      default: _withCtx(() => [
        _createVNode(_component_AppHeading, null, {
          default: _withCtx(() => [
            _hoisted_10
          ]),
          _: 1
        }),
        _createVNode(AppTabs, {
          modelValue: tab.value,
          "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((tab).value = $event)),
          tabs: tabs,
          name: "Tab group"
        }, null, 8, ["modelValue"]),
        _createTextVNode(" " + _toDisplayString(tab.value), 1)
      ]),
      _: 1
    }),
    _createVNode(_component_AppSection, null, {
      default: _withCtx(() => [
        _createVNode(_component_AppHeading, null, {
          default: _withCtx(() => [
            _hoisted_11
          ]),
          _: 1
        }),
        _createVNode(_component_AppGallery, null, {
          default: _withCtx(() => [
            _createVNode(_component_AppStatus, { code: "loading" }, {
              default: _withCtx(() => [
                _hoisted_12
              ]),
              _: 1
            }),
            _createVNode(_component_AppStatus, { code: "success" }, {
              default: _withCtx(() => [
                _hoisted_13
              ]),
              _: 1
            }),
            _createVNode(_component_AppStatus, { code: "warning" }, {
              default: _withCtx(() => [
                _hoisted_14
              ]),
              _: 1
            }),
            _createVNode(_component_AppStatus, { code: "error" }, {
              default: _withCtx(() => [
                _hoisted_15
              ]),
              _: 1
            }),
            _createVNode(_component_AppStatus, { code: "paused" }, {
              default: _withCtx(() => [
                _hoisted_16
              ]),
              _: 1
            }),
            _createVNode(_component_AppStatus, { code: "unknown" }, {
              default: _withCtx(() => [
                _hoisted_17
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }),
    _createVNode(_component_AppSection, null, {
      default: _withCtx(() => [
        _createVNode(_component_AppHeading, null, {
          default: _withCtx(() => [
            _hoisted_18
          ]),
          _: 1
        }),
        _createElementVNode("span", null, _toDisplayString(_unref(omit)(table.value, ["cols", "rows"])), 1),
        _createVNode(AppTable, _mergeProps(table.value, {
          "per-page": table.value.perPage,
          "onUpdate:per-page": _cache[8] || (_cache[8] = ($event: any) => ((table.value.perPage) = $event)),
          start: table.value.start,
          "onUpdate:start": _cache[9] || (_cache[9] = ($event: any) => ((table.value.start) = $event)),
          search: table.value.search,
          "onUpdate:search": _cache[10] || (_cache[10] = ($event: any) => ((table.value.search) = $event)),
          onSort: _cache[11] || (_cache[11] = (value) => (table.value.sort = value)),
          onFilter: _cache[12] || (_cache[12] = 
        (colId, value) => ((table.value.activeFilters || {})[colId] = [...value])
      )
        }), {
          arbitrary: _withCtx(() => [
            _hoisted_19
          ]),
          _: 1
        }, 16, ["per-page", "start", "search"])
      ]),
      _: 1
    })
  ], 64))
}
}

})